/*
  DailyReceiptsTab.jsx — Daily collection receipts (Receipts subview) +
  AI Reconciliation subview + Statement export shortcuts.
  Extracted from CollectionPlanningPage.jsx (Feb 2026 refactor).
*/
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ReceiptIndianRupee, Sparkles, Plus, FileSpreadsheet, MoreVertical, Search,
  Loader2, Edit2, Trash2, Save, X, RefreshCw, Filter, ChevronDown,
  Download, Upload, CheckCircle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import PaginationBar from "@/pages/collection-planning/PaginationBar";
import ProjectSearch from "@/pages/collection-planning/ProjectSearch";
import ReconcilePanel from "@/components/collection/ReconcilePanel";
import FxRateCard from "@/components/collection/FxRateCard";
import {
  fmt, sanitizeNumeric, getDatePreset, RECEIPT_PRESETS,
} from "@/pages/collection-planning/planningUtils";

export default function DailyReceiptsTab({ ctx }) {
  const {
    receiptsSubview, setReceiptsSubview,
    listFilters, setListFilters, listReceipts, listLoading,
    listPage, setListPage, listPageSize, setListPageSize,
    showReceiptDialog, setShowReceiptDialog, receiptForm, setReceiptForm,
    editingReceipt, setEditingReceipt,
    projects, salesMembers, defaultFxRate, saving,
    setShowImportDialog,
    openNewReceipt, openEditReceipt, handleSaveReceipt, deleteReceipt,
    exportReceiptsExcel, handleBankUpload, loadListReceipts,
  } = ctx;

  return (
    <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{receiptsSubview === "reconcile" ? "AI Bank Reconciliation" : "Daily Collection Receipts"}</h1>
                  <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="receipts-subview-toggle">
                    <button type="button" onClick={() => setReceiptsSubview("receipts")} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${receiptsSubview === "receipts" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="subview-receipts-btn">
                      <ReceiptIndianRupee className="w-3.5 h-3.5" /> Receipts
                    </button>
                    <button type="button" onClick={() => setReceiptsSubview("reconcile")} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${receiptsSubview === "reconcile" ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="subview-reconcile-btn">
                      <Sparkles className="w-3.5 h-3.5" /> AI Reconcile
                    </button>
                  </div>
                </div>
                {receiptsSubview === "receipts" && (
                <div className="flex gap-2 flex-wrap items-center">
                  <Button onClick={openNewReceipt} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="new-receipt-btn"><Plus className="w-4 h-4" /> New Receipt</Button>
                  <Button variant="outline" className="gap-2" onClick={exportReceiptsExcel} data-testid="export-receipts-btn"><FileSpreadsheet className="w-4 h-4" /> Export Excel</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-9 w-9" title="Bank actions" data-testid="receipts-bank-menu-btn">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem asChild data-testid="action-bank-statement">
                        <label className="cursor-pointer flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4" /> Upload Bank Statement
                          <input type="file" accept=".xlsx" className="hidden" onChange={handleBankUpload} />
                        </label>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild data-testid="action-bank-template">
                        <a href={`${api.defaults.baseURL}/collections/templates/bank-statement`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                          <Download className="w-4 h-4" /> Bank Template
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowImportDialog(true)} data-testid="action-historical-import">
                        <Upload className="w-4 h-4 mr-2" /> Historical Import
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                )}
              </div>

              {receiptsSubview === "reconcile" ? (
                <ReconcilePanel projects={projects} />
              ) : (
              <>
                {/* Inline Receipt Entry Form (screen-based, replaces old modal) */}
                {showReceiptDialog && (
                  <Card className="border-2 border-[#0066CC] shadow-md">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-base font-bold text-slate-900">{editingReceipt ? "Edit Receipt" : "New Collection Receipt"}</h2>
                          <p className="text-[11px] text-slate-500">{editingReceipt ? `Receipt from ${editingReceipt.receipt_date}` : "Fields marked * are required."}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setShowReceiptDialog(false); setEditingReceipt(null); }} className="text-slate-500" data-testid="close-receipt-form-btn">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Project * <span className="text-[10px] text-slate-400 font-normal">(all statuses searchable)</span></Label>
                            <ProjectSearch projects={projects} value={receiptForm.project_id} onChange={id => setReceiptForm(p => ({ ...p, project_id: id, billing_line_id: "" }))} />
                          </div>
                          <div>
                            <Label className="text-xs">Billing Line <span className="text-red-500">*</span></Label>
                            {(() => {
                              const selProj = projects.find(p => p.id === receiptForm.project_id);
                              const lines = selProj?.billing_lines || [];
                              const sym = (selProj?.currency || "INR") === "USD" ? "$" : "₹";
                              return (
                                <select
                                  data-testid="receipt-billing-line-select"
                                  value={receiptForm.billing_line_id}
                                  onChange={e => setReceiptForm(p => ({ ...p, billing_line_id: e.target.value }))}
                                  className="h-9 w-full text-sm border border-slate-200 rounded-md px-2 bg-white"
                                  disabled={!receiptForm.project_id}
                                >
                                  <option value="">— Not linked —</option>
                                  {lines.map(ln => (
                                    <option key={ln.id} value={ln.id}>
                                      {ln.type} · {ln.frequency.replace("_", " ")} · {sym}{Number(ln.amount || 0).toLocaleString("en-IN")}
                                    </option>
                                  ))}
                                </select>
                              );
                            })()}
                          </div>
                          <div>
                            <Label className="text-xs">Receipt Date *</Label>
                            <Input type="date" value={receiptForm.receipt_date} onChange={e => setReceiptForm(p => ({ ...p, receipt_date: e.target.value }))} data-testid="receipt-date-input" />
                          </div>
                          <div>
                            <Label className="text-xs">Bank Reference</Label>
                            <Input value={receiptForm.bank_reference} onChange={e => setReceiptForm(p => ({ ...p, bank_reference: e.target.value }))} placeholder="e.g. NEFT/RTGS ref" data-testid="receipt-bank-ref-input" />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {(() => {
                            const selProj = projects.find(p => p.id === receiptForm.project_id);
                            const projCurrency = (selProj?.currency || "INR").toUpperCase();
                            const symbol = projCurrency === "USD" ? "$" : "₹";
                            const isUsd = projCurrency === "USD";
                            return (
                              <>
                                <div>
                                  <Label className="text-xs">Collection Amount ({projCurrency}) *</Label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 text-slate-400 font-medium">{symbol}</span>
                                    <Input type="text" inputMode="numeric" autoComplete="off" className="pl-9 h-11 text-base font-medium" value={receiptForm.collection_amount ? String(receiptForm.collection_amount) : ""} onFocus={(e) => e.target.select()} onChange={e => { const c = sanitizeNumeric(e.target.value); setReceiptForm(p => ({ ...p, collection_amount: c === "" ? 0 : parseInt(c, 10) })); }} placeholder="0" data-testid="receipt-amount-input" />
                                  </div>
                                  <p className="text-[10px] text-slate-400 mt-1">
                                    {isUsd
                                      ? `≈ $${((receiptForm.collection_amount || 0) / 1000).toFixed(1)} K`
                                      : `≈ ₹${((receiptForm.collection_amount || 0) / 100000).toFixed(2)} L`}
                                  </p>
                                </div>
                                {isUsd && (
                                  <div className="border border-amber-200 bg-amber-50/60 rounded-md p-2.5">
                                    <Label className="text-xs font-semibold text-amber-800">FX Rate — USD → INR *</Label>
                                    <Input
                                      type="number" step="0.01" min={0}
                                      value={receiptForm.fx_rate_to_inr ?? defaultFxRate}
                                      onChange={e => setReceiptForm(p => ({ ...p, fx_rate_to_inr: e.target.value === "" ? null : Number(e.target.value) }))}
                                      placeholder={String(defaultFxRate)}
                                      className="h-9 mt-1"
                                      data-testid="receipt-fx-rate-input"
                                    />
                                    <p className="text-[10px] text-slate-500 mt-1">
                                      Default is <strong>₹{defaultFxRate}</strong> (from Settings). Override with the actual rate you were credited at.
                                      ≈ INR value: <strong>₹{new Intl.NumberFormat("en-IN").format(Math.round((receiptForm.collection_amount || 0) * (receiptForm.fx_rate_to_inr || defaultFxRate)))}</strong>
                                    </p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          <div>
                            <Label className="text-xs">Sales Team Member *</Label>
                            <select
                              data-testid="receipt-sales-member-select"
                              value={receiptForm.sales_member_id}
                              onChange={e => setReceiptForm(p => ({ ...p, sales_member_id: e.target.value }))}
                              className="h-9 w-full text-sm border border-slate-200 rounded-md px-2 bg-white"
                            >
                              <option value="">— Select who made this collection —</option>
                              {salesMembers.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Notes</Label>
                            <Input value={receiptForm.notes} onChange={e => setReceiptForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional note" data-testid="receipt-notes-input" />
                          </div>
                        </div>
                      </div>

                      {/* ================= Split across Multiple Billing Lines ================= */}
                      {(() => {
                        const selProj = projects.find(p => p.id === receiptForm.project_id);
                        const lines = selProj?.billing_lines || [];
                        if (!selProj || lines.length < 2) return null;
                        const sym = (selProj.currency || "INR") === "USD" ? "$" : "₹";
                        const allocs = receiptForm.allocations || [];
                        const totalAlloc = allocs.reduce((s, a) => s + (Number(a.amount) || 0), 0);
                        const receiptAmt = Number(receiptForm.collection_amount) || 0;
                        const diff = Math.round((receiptAmt - totalAlloc) * 100) / 100;
                        const addAlloc = () => {
                          const used = new Set(allocs.map(a => a.billing_line_id));
                          const nextLine = lines.find(ln => !used.has(ln.id));
                          if (!nextLine) return;
                          const suggested = allocs.length === 0 ? receiptAmt : Math.max(0, diff);
                          setReceiptForm(p => ({ ...p, allocations: [...(p.allocations || []), { billing_line_id: nextLine.id, amount: suggested }] }));
                        };
                        const updateAlloc = (idx, patch) => setReceiptForm(p => ({ ...p, allocations: (p.allocations || []).map((a, i) => i === idx ? { ...a, ...patch } : a) }));
                        const removeAlloc = (idx) => setReceiptForm(p => ({ ...p, allocations: (p.allocations || []).filter((_, i) => i !== idx) }));
                        const autoSplit = () => {
                          if (lines.length === 0 || receiptAmt <= 0) return;
                          const per = Math.floor((receiptAmt * 100) / lines.length) / 100;
                          const rem = Math.round((receiptAmt - per * lines.length) * 100) / 100;
                          const rows = lines.map((ln, i) => ({ billing_line_id: ln.id, amount: i === 0 ? per + rem : per }));
                          setReceiptForm(p => ({ ...p, allocations: rows }));
                        };
                        const fifoSplit = async () => {
                          if (!selProj || receiptAmt <= 0) return;
                          try {
                            const res = await api.get(`/collections/projects/${selProj.id}/billing-lines-outstanding`);
                            const flines = (res.data.lines || []).filter(l => !l.ended && l.outstanding > 0);
                            if (flines.length === 0) {
                              toast.error("No outstanding billing lines to allocate. Try Equal Split or add manually.");
                              return;
                            }
                            // Walk down oldest-first, fill each line's outstanding, remainder to last
                            let remaining = receiptAmt;
                            const rows = [];
                            for (let i = 0; i < flines.length && remaining > 0.001; i++) {
                              const ln = flines[i];
                              const take = Math.min(remaining, ln.outstanding);
                              const rounded = Math.round(take * 100) / 100;
                              rows.push({ billing_line_id: ln.id, amount: rounded });
                              remaining = Math.round((remaining - rounded) * 100) / 100;
                            }
                            // Any excess: attach to the last row (or the earliest line if no rows built)
                            if (remaining > 0.001 && rows.length > 0) {
                              rows[rows.length - 1].amount = Math.round((rows[rows.length - 1].amount + remaining) * 100) / 100;
                              toast.warning(`Receipt amount exceeds outstanding by ₹${remaining.toLocaleString("en-IN")}. Extra added to last line.`);
                            }
                            setReceiptForm(p => ({ ...p, allocations: rows }));
                            toast.success(`FIFO applied across ${rows.length} line${rows.length === 1 ? "" : "s"}`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "FIFO calculation failed");
                          }
                        };
                        return (
                          <div className="mt-4 p-3 border rounded-lg bg-slate-50" data-testid="receipt-allocations-section">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-slate-700">Split across billing lines</p>
                                <span className="text-[10px] text-slate-500">(Optional — leave empty to link to the single line above)</span>
                              </div>
                              <div className="flex gap-2">
                                {allocs.length > 0 && (
                                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px] text-red-600" onClick={() => setReceiptForm(p => ({ ...p, allocations: [] }))} data-testid="receipt-allocations-clear">Clear Split</Button>
                                )}
                                {allocs.length === 0 && receiptAmt > 0 && (
                                  <>
                                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={fifoSplit} data-testid="receipt-allocations-fifo" title="Auto-fill oldest billing line first, then next, until receipt amount is exhausted">
                                      FIFO Auto Adjust
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={autoSplit} data-testid="receipt-allocations-auto">Equal Split</Button>
                                  </>
                                )}
                                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={addAlloc} disabled={allocs.length >= lines.length} data-testid="receipt-allocations-add"><Plus className="w-3 h-3" /> Add Line</Button>
                              </div>
                            </div>
                            {allocs.length === 0 ? (
                              <p className="text-[11px] text-slate-500 italic">Click &quot;Add Line&quot; or &quot;Equal Split&quot; to distribute this receipt across multiple billing lines of the selected project.</p>
                            ) : (
                              <div className="space-y-1.5">
                                {allocs.map((a, idx) => {
                                  const used = new Set(allocs.filter((_, i) => i !== idx).map(x => x.billing_line_id));
                                  return (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                      <select
                                        className="col-span-5 h-8 text-xs border border-slate-200 rounded px-2 bg-white"
                                        value={a.billing_line_id}
                                        onChange={e => updateAlloc(idx, { billing_line_id: e.target.value })}
                                        data-testid={`receipt-allocation-line-${idx}`}
                                      >
                                        <option value="">— Select billing line —</option>
                                        {lines.filter(ln => ln.id === a.billing_line_id || !used.has(ln.id)).map(ln => (
                                          <option key={ln.id} value={ln.id}>
                                            {ln.type} · {ln.frequency.replace("_", " ")} · {sym}{Number(ln.amount || 0).toLocaleString("en-IN")}
                                          </option>
                                        ))}
                                      </select>
                                      <select
                                        className="col-span-3 h-8 text-xs border border-slate-200 rounded px-2 bg-white truncate"
                                        value={a.sales_member_id || ""}
                                        onChange={e => updateAlloc(idx, { sales_member_id: e.target.value })}
                                        data-testid={`receipt-allocation-owner-${idx}`}
                                        title="Sales owner for this allocation. Defaults to receipt-level owner if left blank."
                                      >
                                        <option value="">— Same as receipt —</option>
                                        {salesMembers.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
                                      </select>
                                      <div className="col-span-3 relative">
                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">{sym}</span>
                                        <Input
                                          type="text"
                                          inputMode="numeric"
                                          className="h-8 pl-6 text-xs font-mono text-right"
                                          value={a.amount ? String(a.amount) : ""}
                                          onFocus={(e) => e.target.select()}
                                          onChange={e => { const c = sanitizeNumeric(e.target.value); updateAlloc(idx, { amount: c === "" ? 0 : parseFloat(c) }); }}
                                          data-testid={`receipt-allocation-amount-${idx}`}
                                        />
                                      </div>
                                      <Button type="button" size="sm" variant="ghost" className="col-span-1 h-8 w-8 p-0 text-red-500 justify-self-end" onClick={() => removeAlloc(idx)} data-testid={`receipt-allocation-remove-${idx}`}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  );
                                })}
                                <div className={`flex items-center justify-between text-[11px] font-medium pt-1.5 border-t mt-1 ${Math.abs(diff) < 0.01 ? "text-emerald-700" : "text-red-600"}`} data-testid="receipt-allocations-total">
                                  <span>Total allocated</span>
                                  <span className="font-mono">
                                    {sym}{totalAlloc.toLocaleString("en-IN")}
                                    {Math.abs(diff) >= 0.01 && (
                                      <span className="ml-2 font-normal">({diff > 0 ? `${sym}${Math.abs(diff).toLocaleString("en-IN")} unallocated` : `${sym}${Math.abs(diff).toLocaleString("en-IN")} over`})</span>
                                    )}
                                    {Math.abs(diff) < 0.01 && <CheckCircle className="w-3.5 h-3.5 inline-block ml-1.5 text-emerald-600" />}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="flex gap-2 mt-4 pt-3 border-t">
                        <Button onClick={handleSaveReceipt} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-receipt-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                          {editingReceipt ? "Update Receipt" : "Record Receipt"}
                        </Button>
                        <Button variant="outline" onClick={() => { setShowReceiptDialog(false); setEditingReceipt(null); }} data-testid="cancel-receipt-form-btn">Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="border-slate-200">
                    <CardContent className="p-4 space-y-3">
                      {/* Quick date chips */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mr-1">Quick:</span>
                        {RECEIPT_PRESETS.map(p => {
                          const r = getDatePreset(p.id);
                          const isActive = listFilters.start === r.start && listFilters.end === r.end;
                          return (
                            <button
                              key={p.id}
                              data-testid={`receipts-preset-${p.id}`}
                              onClick={() => { setListFilters(f => ({ ...f, start: r.start, end: r.end })); setTimeout(() => loadListReceipts(), 30); }}
                              className={`px-2.5 py-1 text-[11px] rounded-full border transition ${isActive ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC]"}`}
                            >
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                        <div>
                          <Label className="text-[11px] text-slate-500 mb-1 block">From Date</Label>
                          <Input data-testid="list-receipts-start" type="date" value={listFilters.start} onChange={e => setListFilters(p => ({ ...p, start: e.target.value }))} className="h-9 text-xs" />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500 mb-1 block">To Date</Label>
                          <Input data-testid="list-receipts-end" type="date" value={listFilters.end} onChange={e => setListFilters(p => ({ ...p, end: e.target.value }))} className="h-9 text-xs" />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500 mb-1 block">Project</Label>
                          <ProjectSearch projects={projects} value={listFilters.project_id} onChange={(id) => setListFilters(p => ({ ...p, project_id: id }))} placeholder="All projects" />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500 mb-1 block">Search</Label>
                          <div className="relative">
                            <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
                            <Input data-testid="list-receipts-search" type="text" value={listFilters.q} onChange={e => setListFilters(p => ({ ...p, q: e.target.value }))} placeholder="Project / bank ref / notes" className="h-9 text-xs pl-7" />
                          </div>
                        </div>
                        <Button variant="outline" size="icon" onClick={loadListReceipts} className="h-9 w-9" title="Refresh" data-testid="receipts-refresh-btn">
                          <RefreshCw className={`w-3.5 h-3.5 ${listLoading ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {(() => {
                    const q = (listFilters.q || "").toLowerCase().trim();
                    const filtered = q
                      ? listReceipts.filter(r =>
                          (r.project_name || "").toLowerCase().includes(q) ||
                          (r.billing_type || "").toLowerCase().includes(q) ||
                          (r.bank_reference || "").toLowerCase().includes(q) ||
                          (r.notes || "").toLowerCase().includes(q))
                      : listReceipts;
                    const totalAmt = filtered.reduce((s, r) => s + (r.collection_amount || 0), 0);
                    return (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="bg-white rounded-lg border p-3 text-center">
                            <p className="text-xl font-bold text-slate-800">{filtered.length}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Receipts</p>
                          </div>
                          <div className="bg-white rounded-lg border p-3 text-center">
                            <p className="text-xl font-bold text-emerald-600">{fmt(totalAmt)}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Collected</p>
                          </div>
                          <div className="bg-white rounded-lg border p-3 text-center">
                            <p className="text-xl font-bold text-[#0066CC]">{fmt(filtered.length ? totalAmt / filtered.length : 0)}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Avg / Receipt</p>
                          </div>
                          <div className="bg-white rounded-lg border p-3 text-center">
                            <p className="text-xl font-bold text-slate-800">{new Set(filtered.map(r => r.project_id)).size}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Projects</p>
                          </div>
                        </div>

                        <Card className="border-slate-200">
                          <CardContent className="p-0">
                            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                              <table className="w-full text-xs" data-testid="list-receipts-table">
                                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                                  <tr>
                                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Date</th>
                                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Project</th>
                                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Billing</th>
                                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Sales Person</th>
                                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Bank Ref</th>
                                    <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Notes</th>
                                    <th className="text-right py-2.5 px-3 font-semibold text-slate-600">Amount</th>
                                    <th className="text-center py-2.5 px-3 font-semibold text-slate-600 w-[80px]">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {listLoading ? (
                                    <tr><td colSpan="7" className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                                  ) : filtered.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center py-12 text-slate-400">No receipts match these filters.</td></tr>
                                  ) : (
                                    (() => {
                                      const start = (listPage - 1) * listPageSize;
                                      const pageSlice = filtered.slice(start, start + listPageSize);
                                      return pageSlice.map(r => (
                                        <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                                          <td className="py-2.5 px-3 text-slate-700 whitespace-nowrap">{new Date(r.receipt_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                                          <td className="py-2.5 px-3 font-medium text-slate-900">{r.project_name}</td>
                                          <td className="py-2.5 px-3 text-slate-700">
                                            {r.billing_type ? (
                                              <span className="text-[11px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded">{r.billing_type}{r.billing_frequency ? ` · ${r.billing_frequency.replace("_", " ")}` : ""}</span>
                                            ) : <span className="text-slate-300">—</span>}
                                            {Array.isArray(r.allocations) && r.allocations.length > 1 && (
                                              <span className="ml-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded" title={`Split across ${r.allocations.length} billing lines`}>Split · {r.allocations.length}</span>
                                            )}
                                          </td>
                                          <td className="py-2.5 px-3 text-slate-700">
                                            {r.sales_member_name ? (
                                              <span className="text-[11px] font-medium bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{r.sales_member_name}</span>
                                            ) : <span className="text-[10px] text-amber-600">Unassigned</span>}
                                          </td>
                                          <td className="py-2.5 px-3 text-blue-600">{r.bank_reference || "—"}</td>
                                          <td className="py-2.5 px-3 text-slate-500 max-w-[260px] truncate" title={r.notes}>{r.notes || "—"}</td>
                                          <td className="py-2.5 px-3 text-right font-bold text-emerald-600 whitespace-nowrap">{fmt(r.collection_amount)}</td>
                                          <td className="py-2.5 px-3 text-center">
                                            <div className="flex justify-center gap-0.5">
                                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#0066CC]" onClick={() => openEditReceipt(r)} data-testid={`edit-receipt-${r.id}`} title="Edit"><Edit2 className="w-3.5 h-3.5" /></Button>
                                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteReceipt(r.id)} data-testid={`delete-receipt-${r.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                                            </div>
                                          </td>
                                        </tr>
                                      ));
                                    })()
                                  )}
                                </tbody>
                                {filtered.length > 0 && (
                                  <tfoot className="bg-slate-50 border-t border-slate-200 sticky bottom-0 z-10 shadow-[0_-1px_0_rgba(0,0,0,0.06)]">
                                    <tr>
                                      <td colSpan="6" className="py-2.5 px-3 font-semibold text-slate-700">Total ({filtered.length} receipt{filtered.length !== 1 ? "s" : ""})</td>
                                      <td className="py-2.5 px-3 text-right font-bold text-emerald-700">{fmt(totalAmt)}</td>
                                      <td></td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                            {filtered.length > 0 && (
                              <PaginationBar
                                total={filtered.length}
                                page={listPage}
                                pageSize={listPageSize}
                                onPageChange={setListPage}
                                onPageSizeChange={setListPageSize}
                                testId="list-receipts-pagination"
                              />
                            )}
                          </CardContent>
                        </Card>
                      </>
                    );
                  })()}
                </>
              )}
            </div>

  );
}
